import {
  ContractStatusEnum,
  IBillGroupResp,
  IBillingScheduleRespSchemaUI,
  IContract,
} from '@monetize/types/app';
import {
  DEFAULT_PAGER,
  FilterType,
  GetListApiFilter,
  TDataTablePager,
} from '@monetize/types/common';
import { toDateShort } from '@monetize/utils/core';
import {
  AppColumnDef,
  ExpandedState,
  OnChangeFn,
  createColumnHelper,
} from '../../../../components/Monetize/DataTableNew/appTable';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';
import { useNavigate } from 'react-router';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  doGetBillingScheduleByContractId,
  useGetContracts,
} from '../../../../api/cpqService';
import {
  MBox,
  MCustomIconButton,
  MFlex,
  MHStack,
  MText,
} from '../../../../components/Monetize';
import { MDataTableFilter } from '../../../../components/Monetize/DataTable';
import DataTableActions from '../../../../components/Monetize/DataTable/MDataTableActions';
import { DataTable } from '../../../../components/Monetize/DataTableNew/DataTable';
import {
  ACTION_COLUMN_KEY,
  createActionColumn,
} from '../../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { MCurrency } from '../../../../components/Monetize/MCurrency';
import { ROUTES } from '../../../../constants';
import { CONTRACT_STATUS_DISPLAY } from '../../../../constants/contracts';
import { buildFilterParamsRequestObject } from '../../../../utils';
import { CONTRACT_FILTER_OPTIONS_FOR_ACCOUNT_DETAIL } from '../../../Sales/ContractListMain';
import { BillingScheduleDownloadButton } from '../components/BillingScheduleDownloadButton';
import { InvoicesUnderContractTable } from '../components/InvoicesUnderContractTable';
import { downloadInvoices } from '../utils';

const PAGER: TDataTablePager = {
  ...DEFAULT_PAGER,
  sortField: 'startDate',
  sortOrder: -1,
  rows: 100,
};

const columnHelper = createColumnHelper<IContract>();

const useContractsByBillGroupId = (billGroupId: string) => {
  const [filters, setFilters] = useState<FilterType[]>([]);
  const [searchKey] = useState<string>('id');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pager, setPager] = useState<TDataTablePager>(PAGER);
  const [filterParams, setFilterParams] = useState<GetListApiFilter>(() =>
    buildFilterParamsRequestObject(filters, searchTerm, searchKey),
  );

  useEffect(() => {
    if (filters || searchTerm || pager) {
      setFilterParams(
        buildFilterParamsRequestObject(filters, searchTerm, searchKey),
      );
    }
  }, [filters, searchTerm, pager, searchKey]);

  const {
    isLoading,
    isFetched,
    isRefetching,
    data: contracts,
  } = useGetContracts(
    {
      config: pager,
      filters: billGroupId ? { ...filterParams, billGroupId } : filterParams,
      url: `/api/contracts`,
    },
    { meta: { showErrorToast: true } },
  );

  const onResetFilter = () => {
    setFilters([]);
  };

  return {
    pager,
    setPager,
    filters,
    setFilters,
    onResetFilter,
    searchKey,
    searchTerm,
    setSearchTerm,
    totalRecords: contracts?.totalElements || 0,
    contracts,
    loading: isLoading || (isRefetching && !isFetched),
  };
};

interface BillGroupBillingScheduleTabProps {
  billGroup: IBillGroupResp;
}

export const BillGroupBillingScheduleTab = ({
  billGroup,
}: BillGroupBillingScheduleTabProps) => {
  const navigate = useNavigate();
  const filterComponentReset = useRef<any>(null);

  const {
    filters,
    setFilters,
    searchKey,
    searchTerm,
    onResetFilter,
    contracts,
    pager,
    loading,
  } = useContractsByBillGroupId(billGroup.id);

  const [billingScheduleLoading, setBillingScheduleLoading] = useState<
    Set<string>
  >(new Set());
  const [billingScheduleByContractId, setBillingScheduleByContractId] =
    useState<Record<string, IBillingScheduleRespSchemaUI>>({});
  const [isUsageProductExists, setIsUsageProductExists] =
    useState<boolean>(false);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const billingScheduleRef = useRef(billingScheduleByContractId);
  billingScheduleRef.current = billingScheduleByContractId;

  const billingScheduleLoadingRef = useRef(billingScheduleLoading);
  billingScheduleLoadingRef.current = billingScheduleLoading;

  const fetchBillingSchedule = useCallback(async (contractId: string) => {
    if (
      billingScheduleRef.current[contractId] ||
      billingScheduleLoadingRef.current.has(contractId)
    ) {
      return;
    }

    setBillingScheduleLoading((prev) => new Set([...prev, contractId]));
    try {
      const res = await doGetBillingScheduleByContractId(contractId);
      setBillingScheduleByContractId((prev) => ({
        ...prev,
        [contractId]: res,
      }));
      if (res.usageProductExists) {
        setIsUsageProductExists(true);
      }
    } catch (error) {
      handleApiErrorToast(error);
    } finally {
      setBillingScheduleLoading((prev) => {
        const next = new Set(prev);
        next.delete(contractId);
        return next;
      });
    }
  }, []);

  const handleExpandedChange: OnChangeFn<ExpandedState> = useCallback(
    (updater) => {
      setExpanded((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;

        // Schedule fetches outside the state updater to avoid side effects during render
        queueMicrotask(() => {
          if (next === true) {
            // Expand all — fetch for all contracts not yet loaded
            contracts?.content.forEach((contract) => {
              fetchBillingSchedule(contract.id);
            });
          } else if (typeof next === 'object') {
            // Find newly expanded rows and fetch their billing schedules
            Object.keys(next).forEach((key) => {
              if (next[key] && !(typeof prev === 'object' && prev[key])) {
                fetchBillingSchedule(key);
              }
            });
          }
        });

        return next;
      });
    },
    [contracts, fetchBillingSchedule],
  );

  const actionBodyTemplate = useCallback(
    (data: IContract) => {
      const actions = [
        {
          title: 'View Contract',
          enabled: true,
          action: () => {
            navigate(
              ROUTES.getBillingScheduleContractsEditRoute(
                billGroup.id,
                data.id,
              ),
            );
          },
        },
        {
          title: 'Export CSV',
          enabled: true,
          action: () => {
            downloadInvoices({ billGroup, contractIds: [data.id] });
          },
        },
      ];
      return <DataTableActions actions={actions} />;
    },
    [billGroup, navigate],
  );

  const columns: AppColumnDef<IContract, any>[] = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'Name',
        size: 300,
        cell: ({ row, getValue }) => {
          const isExpanded = row.getIsExpanded();
          return (
            <MFlex gap={2} align="center">
              <MCustomIconButton
                icon={isExpanded ? MdExpandLess : MdExpandMore}
                btnSize={5}
                onClick={(event) => {
                  event.stopPropagation();
                  row.toggleExpanded();
                }}
                _focus={{ bg: 'transparent' }}
                aria-label={
                  isExpanded ? `Collapse row ${row.id}` : `Expand row ${row.id}`
                }
              />
              <MText wordBreak="break-word">{getValue()}</MText>
            </MFlex>
          );
        },
      }),
      columnHelper.accessor('startDate', {
        header: 'Service Period',
        size: 200,
        cell: ({ row }) => (
          <MText>
            {toDateShort(row.original.startDate)} -{' '}
            {toDateShort(row.original.endDate)}
          </MText>
        ),
      }),
      columnHelper.accessor('totalValue', {
        header: 'Amount',
        size: 140,
        cell: ({ getValue, row }) => (
          <MCurrency
            options={{ currency: row.original.currency }}
            amount={getValue()}
          />
        ),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        size: 120,
        cell: ({ getValue }) => (
          <MText>
            {CONTRACT_STATUS_DISPLAY[getValue() as ContractStatusEnum]}
          </MText>
        ),
      }),
      createActionColumn<IContract>(actionBodyTemplate),
    ],
    [actionBodyTemplate],
  );

  const contractIds: string[] =
    contracts?.content.map((contract: { id: string }) => contract.id) ?? [];

  return (
    <MBox>
      <MFlex mb={6} justify="space-between">
        <MBox>
          <MText>Onetime invoices are not shown on this screen</MText>
          {isUsageProductExists && (
            <MText>
              *Excludes usage. Usage is calculated at time of Invoice
              generation.
            </MText>
          )}
        </MBox>
        <MFlex>
          <MHStack gap="2">
            <MDataTableFilter
              filters={filters}
              filterOptions={CONTRACT_FILTER_OPTIONS_FOR_ACCOUNT_DETAIL}
              setFilters={setFilters}
              onResetFilter={onResetFilter}
              resetFilter={filterComponentReset}
            />
            <BillingScheduleDownloadButton
              entity="billingSchedule"
              contractIds={contractIds}
              billGroup={billGroup}
              filters={filters}
              searchKey={searchKey}
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
            />
          </MHStack>
        </MFlex>
      </MFlex>

      <DataTable
        columns={columns}
        data={contracts?.content || []}
        enablePagination={false}
        onRowClick={(_row: IContract, tableRow) => {
          tableRow.toggleExpanded();
        }}
        isLoading={loading}
        emptyProps={{
          mainMessage: 'No contracts in this bill group',
          smallMessage: 'If any active contracts exist, they will show up here',
        }}
        clientControlled
        state={{ expanded }}
        onExpandedChange={handleExpandedChange}
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        customChildRow={{
          getRowCanExpand: () => true,
          renderCustomExpandedRow: (row) => (
            <InvoicesUnderContractTable
              periods={billingScheduleByContractId[row.original.id]?.periods}
              billGroupCurrency={row.original.currency}
              billingScheduleLoading={billingScheduleLoading.has(
                row.original.id,
              )}
            />
          ),
        }}
      />
    </MBox>
  );
};
